import { Zap, ArrowRight, Globe } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import type { ExecutiveSummary, GrantProgram, ViewMode } from '@/types/roadmap.types';

interface Props {
  executiveSummary: ExecutiveSummary;
  programs: GrantProgram[];
  mode: ViewMode;
}

const PHASE_CONFIG = [
  {
    key: 'now' as const,
    label: 'Primary',
    subtitle: 'Highest priority — act immediately',
    icon: Zap,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
  },
  {
    key: 'next' as const,
    label: 'Go',
    subtitle: 'Active pursuit — next in sequence',
    icon: ArrowRight,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
] as const;

export default function ExecutiveActionSlide({ executiveSummary, programs, mode }: Props) {
  const { action_plan, poland_note } = executiveSummary;

  const nameByProgramId = new Map(
    programs.map((p) => [p.id, p.program_name]),
  );

  return (
    <Slide mode={mode}>
      <div className="w-full mx-auto text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Executive Decision Summary
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-8 print:mb-3 print:text-[16pt]">
          Action Plan
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto mb-4 print:grid-cols-2 print:mb-2 print:gap-2">
          {PHASE_CONFIG.map((phase) => {
            const items = action_plan[phase.key];
            if (items.length === 0) return null;
            const Icon = phase.icon;
            return (
              <Card
                key={phase.key}
                className="rounded-lg shadow-sm border-primary/20 text-left print:shadow-none print:break-inside-avoid print:rounded-md"
              >
                <CardHeader className="pb-3 print:pb-1 print:p-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 print:w-6 print:h-6 ${phase.iconBg}`}>
                      <Icon className={`w-5 h-5 print:w-3 print:h-3 ${phase.iconColor}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold leading-tight print:text-[9pt]">
                        {phase.label}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground print:text-[7pt]">
                        {phase.subtitle}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="print:p-3 print:pt-0">
                  <ul className="space-y-2.5 print:space-y-1">
                    {items.map((item) => (
                      <li key={item.program} className="text-sm print:text-[7.5pt]">
                        <p className="font-semibold text-foreground leading-snug print:leading-tight">{nameByProgramId.get(item.program) ?? item.program}</p>
                        <p className="text-muted-foreground leading-snug mt-0.5 print:leading-tight print:mt-0">{item.action}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {poland_note.length > 0 && (
          <Card className="rounded-lg shadow-sm border-violet-200 bg-violet-50/30 text-left max-w-5xl mx-auto print:shadow-none print:break-inside-avoid print:rounded-md">
            <CardHeader className="pb-2 print:pb-0.5 print:p-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-violet-100 print:w-6 print:h-6">
                  <Globe className="w-5 h-5 text-violet-600 print:w-3 print:h-3" />
                </div>
                <CardTitle className="text-lg font-bold print:text-[9pt]">
                  Poland / FENG Strategic Note
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="print:p-3 print:pt-0">
              <ul className="space-y-1.5 print:space-y-0.5">
                {poland_note.map((note, i) => (
                  <li
                    key={i}
                    className="text-sm leading-relaxed text-muted-foreground print:text-[7.5pt] print:leading-snug flex gap-2"
                  >
                    <span className="text-violet-400 shrink-0 mt-0.5 print:mt-0">&#x2022;</span>
                    {note}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="max-w-5xl mx-auto mt-6 text-left print:mt-3">
          <h3 className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase mb-2 print:text-[7pt]">
            What this means in practice
          </h3>
          <ul className="text-sm text-muted-foreground/90 space-y-1 leading-relaxed print:text-[7.5pt] print:space-y-0.5">
            <li>Primary programs are the highest-leverage moves to pursue first.</li>
            <li>Go programs form the second wave once Primary applications are underway.</li>
            <li>Together they balance near-term wins with longer-term strategic positioning.</li>
          </ul>
        </div>
      </div>
    </Slide>
  );
}
